/* eslint-disable react/jsx-props-no-spreading */
import { IventisRowNode, RowNetworkStatus, TimeCellEditorRef, SubheaderListItemCellEditorProps } from "@iventis/data-table";
import React, { forwardRef, useImperativeHandle, useRef } from "react";
import { ScheduleConfigVenueLocationType } from "@iventis/domain-model/model/scheduleConfigVenueLocationType";
import { VenueLocation } from "@iventis/domain-model/model/venueLocation";
import { EMPTY_GUID, isNullUndefinedOrEmptyGuid } from "@iventis/utilities";
import { SubheaderListItemCellEditor } from "@iventis/data-table/components/subheader-list-item-cell-editor";
import { Content } from "@iventis/translations";

export interface VenueLocationRowData extends VenueLocation {
    canEdit: boolean;
    networkStatus: RowNetworkStatus;
}

export type VenueLocationRowNode = IventisRowNode<VenueLocationRowData>;

export const topLevelVenueOptionId = EMPTY_GUID;

// TODO: Translate
export const topLevelVenueOption = { id: topLevelVenueOptionId, name: "Top Level" };

export const isTopLevelVenue = (data: VenueLocationRowData) => isNullUndefinedOrEmptyGuid(data.type?.allowedParentType) && isNullUndefinedOrEmptyGuid(data.parentId);

export const isParentIdFieldValid = (node: IventisRowNode<VenueLocationRowData>) => isVenueParentIdValid(node.data.type, node.data.parentId);

export const isVenueParentIdValid = (venueType: ScheduleConfigVenueLocationType, parentId: string) => {
    if (venueType === undefined) {
        return false;
    }
    if (isNullUndefinedOrEmptyGuid(venueType.allowedParentType)) {
        return isNullUndefinedOrEmptyGuid(parentId);
    }

    return !isNullUndefinedOrEmptyGuid(parentId);
};

export const getVenueChildren = (allVenues: VenueLocationRowData[], idOfParent: string) =>
    allVenues.filter((row) => !isNullUndefinedOrEmptyGuid(row.parentId) && row.parentId === idOfParent);

export const rowToVenueLocation = (row: VenueLocationRowData): VenueLocation => ({
    id: row.id,
    name: row.name,
    scheduleId: row.scheduleId,
    type: row.type,
    parentId: row.parentId,
    city: "",
    dataFieldValues: row.dataFieldValues,
});

export const VenueLocationParentIdCellEditor = forwardRef<TimeCellEditorRef, SubheaderListItemCellEditorProps<VenueLocationRowData>>((props, ref) => {
    const listItemCellEditorRef = useRef<TimeCellEditorRef>();

    const errorSubmitted = useRef<boolean>(false);
    const { translate } = props.context;

    useImperativeHandle(ref, () => ({
        // the final value to send to the grid, on completion of editing
        getValue() {
            return listItemCellEditorRef.current?.getValue();
        },

        // Gets called once before editing starts, to give editor a chance to
        // cancel the editing before it even starts.
        isCancelBeforeStart() {
            const rowsData: VenueLocationRowData[] = [];
            props.api.forEachNode((node) => {
                rowsData.push(node.data);
            });
            const childrenOfThisVenue = getVenueChildren(rowsData, props.data.id);
            if (childrenOfThisVenue.length > 0 && !errorSubmitted.current) {
                errorSubmitted.current = true;
                props.context.toast.warning({
                    title: translate(Content.common.warning),
                    message: translate(Content.schedule2.venueEdit.tryEditParent, { count: childrenOfThisVenue.length }),
                });
                return true;
            }
            return listItemCellEditorRef.current?.isCancelBeforeStart();
        },

        // Gets called once when editing is finished (eg if Enter is pressed).
        // If you return true, then the result of the edit will be ignored.
        isCancelAfterEnd() {
            return listItemCellEditorRef.current?.isCancelAfterEnd();
        },
    }));

    return <SubheaderListItemCellEditor<VenueLocationRowData> {...props} ref={listItemCellEditorRef} />;
});
